import type { Group, GroupEvent } from './firestore'

/** Every admin of a group: the creator (always, implicitly) plus the promoted members. */
export function groupAdminIds(group: Pick<Group, 'createdBy' | 'adminIds'>): string[] {
  return [...new Set([group.createdBy, ...(group.adminIds ?? [])])]
}

export function isGroupAdmin(group: Pick<Group, 'createdBy' | 'adminIds' | 'memberIds'>, uid: string | null | undefined): boolean {
  if (!uid || !group.memberIds.includes(uid)) return false
  return groupAdminIds(group).includes(uid)
}

/** Whether `uid` links events straight to the group, rather than suggesting them to its admins. */
export function canLinkDirectly(group: Pick<Group, 'createdBy' | 'adminIds' | 'memberIds' | 'requireLinkApproval'>, uid: string | null | undefined): boolean {
  if (!uid || !group.memberIds.includes(uid)) return false
  return !group.requireLinkApproval || isGroupAdmin(group, uid)
}

/**
 * Whether `uid` may unlink an event: admins always; anyone else only their own
 * link (undoing a mistake) while no other member has marked interest in it.
 */
export function canUnlinkGroupEvent(
  group: Pick<Group, 'createdBy' | 'adminIds' | 'memberIds'>,
  groupEvent: Pick<GroupEvent, 'createdBy'>,
  uid: string | null | undefined,
  othersInterested: boolean,
): boolean {
  if (isGroupAdmin(group, uid)) return true
  return !!uid && group.memberIds.includes(uid) && groupEvent.createdBy === uid && !othersInterested
}

export function groupEventRequestId(groupId: string, eventId: string): string {
  return `${groupId}_${eventId}`
}

/**
 * Whether `viewer` may take `target`'s admin role away. Admins can step down;
 * only the creator demotes someone else, since an admin who could demote
 * another admin could then remove them.
 */
export function canDemoteAdmin(group: Pick<Group, 'createdBy' | 'adminIds' | 'memberIds'>, viewer: string | null | undefined, target: string): boolean {
  if (!isGroupAdmin(group, viewer) || target === group.createdBy) return false
  return target === viewer || viewer === group.createdBy
}

/** Whether `viewer` may remove `target` from the group: admins remove members; only the creator removes admins. */
export function canRemoveMember(group: Pick<Group, 'createdBy' | 'adminIds' | 'memberIds'>, viewer: string | null | undefined, target: string): boolean {
  if (!isGroupAdmin(group, viewer) || target === viewer || target === group.createdBy) return false
  if (!group.memberIds.includes(target)) return false
  return viewer === group.createdBy || !groupAdminIds(group).includes(target)
}
